import { useCallback, useEffect, useRef } from 'react';

interface UseGamificationStateRefreshOptions {
  enabled: boolean;
  refreshAccountDetails: () => Promise<unknown>;
  refreshUserSettings: () => Promise<unknown>;
  refetchApprovedGameGoals?: () => void | Promise<void>;
  shouldRefetchApprovedGoals?: () => boolean;
  shouldRefresh?: () => boolean;
  refetchTriggers?: readonly unknown[];
}

export const useGamificationStateRefresh = ({
  enabled,
  refreshAccountDetails,
  refreshUserSettings,
  refetchApprovedGameGoals,
  shouldRefetchApprovedGoals,
  shouldRefresh,
  refetchTriggers = [],
}: UseGamificationStateRefreshOptions) => {
  const refreshGamificationState = useCallback(async () => {
    if (shouldRefresh && !shouldRefresh()) {
      return;
    }

    const approvedGoalsRefresh =
      refetchApprovedGameGoals && shouldRefetchApprovedGoals?.() ? refetchApprovedGameGoals() : Promise.resolve();

    await Promise.all([refreshAccountDetails(), refreshUserSettings(), approvedGoalsRefresh]);
  }, [refetchApprovedGameGoals, refreshAccountDetails, refreshUserSettings, shouldRefetchApprovedGoals, shouldRefresh]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      void refreshGamificationState();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, refreshGamificationState]);

  const hasSkippedInitialTriggerRef = useRef(false);

  useEffect(() => {
    if (!enabled || refetchTriggers.length === 0) {
      return;
    }

    if (!hasSkippedInitialTriggerRef.current) {
      hasSkippedInitialTriggerRef.current = true;
      return;
    }

    void refreshGamificationState();
    // Refetch when external triggers change (e.g. Games page tab switch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refreshGamificationState, ...refetchTriggers]);

  return { refreshGamificationState };
};
